import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { Parallax } from 'react-scroll-parallax';
import Slider from 'react-slick';
import { StaticImage } from 'gatsby-plugin-image';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Accordion from '../components/Accordion';
import Testimonial from '../components/Testimonial';
import CareerBlock from '../components/CareerBlock';
import { useDownloadApp } from '../utils/download';
import Counter from '../components/Counter';

const WorkNextArrow = ({ className, onClick }) => (
  <div
    className={className}
    onClick={onClick}
    onKeyDown={onClick}
    role="button"
    tabIndex="0">
    <svg
      x="0px"
      y="0px"
      viewBox="0 0 9.3 15.1"
      style={{ enableBackground: 'new 0 0 9.3 15.1' }}>
      <path
        d="M9.3,7.6c0,0.3-0.1,0.5-0.3,0.7l-7,6.5c-0.4,0.4-1.2,0.4-1.6,0c-0.4-0.4-0.4-1.1,0-1.5l6.2-5.7L0.3,1.8
c-0.4-0.4-0.4-1.1,0-1.5c0.4-0.4,1.2-0.4,1.6,0l7,6.5C9.2,7,9.3,7.3,9.3,7.6z"
      />
    </svg>
  </div>
);

WorkNextArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
};

const WorkPrevArrow = ({ className, onClick }) => (
  <div
    className={className}
    onClick={onClick}
    onKeyDown={onClick}
    role="button"
    tabIndex="0">
    <svg
      x="0px"
      y="0px"
      viewBox="0 0 9.3 15.1"
      style={{ enableBackground: 'new 0 0 9.3 15.1' }}>
      <path
        d="M0.3,6.8l7-6.5c0.4-0.4,1.2-0.4,1.6,0c0.4,0.4,0.4,1.1,0,1.5L2.8,7.6L9,13.3c0.4,0.4,0.4,1.1,0,1.5c-0.4,0.4-1.2,0.4-1.6,0
l-7-6.5C0.1,8.1,0,7.8,0,7.6C0,7.3,0.1,7,0.3,6.8z"
      />
    </svg>
  </div>
);

WorkPrevArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
};

const settingsHeroCards = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 1000,
  cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)',
  slidesToShow: 1,
  slidesToScroll: 1,
  fade: true,
  autoplay: true,
  autoplaySpeed: 2000,
};
const clientSlider = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 4,
  slidesToScroll: 1,
  fade: true,
  autoplay: true,
  autoplaySpeed: 4000,
};

const settingsWork = {
  dots: false,
  infinite: false,
  speed: 1100,
  slidesToShow: 3,
  slidesToScroll: 1,
  nextArrow: <WorkNextArrow />,
  prevArrow: <WorkPrevArrow />,
  responsive: [
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const HeroSection = () => {
  const { popupAppClick } = useDownloadApp();

  return (
    <section>
      <div className="container">
        <div className="hero-content">
          <h1>
            Simplifying communication and collaboration for the construction
            industry
          </h1>
          <button type="button" className="button" onClick={popupAppClick}>
            Download app
          </button>
          <p>
            Available on
            <a
              href="https://play.google.com/store/apps/details?id=in.powerplay.android.fieldapp&hl=en_IN"
              target="_blank"
              rel="noopener noreferrer">
              <svg
                x="0px"
                y="0px"
                viewBox="0 0 19 20"
                style={{ enableBackground: 'new 0 0 19 20' }}>
                <g>
                  <path
                    className="store-icon"
                    d="M3.1,0.3C2.6,0,2.1-0.1,1.6,0.1l9,9l2.9-2.9L3.1,0.3z"
                  />
                  <path
                    className="store-icon"
                    d="M0.4,0.8C0.2,1.1,0,1.5,0,2V18c0,0.4,0.1,0.8,0.4,1.2L9.6,10L0.4,0.8z"
                  />
                  <path
                    className="store-icon"
                    d="M17.3,8.3l-2.6-1.5L11.5,10l3.2,3.2l2.6-1.5c0.6-0.4,1-1,1-1.7C18.3,9.3,17.9,8.6,17.3,8.3z"
                  />
                  <path
                    className="store-icon"
                    d="M10.6,10.9l-9,9C1.7,20,1.9,20,2,20c0.4,0,0.7-0.1,1.1-0.3l10.4-5.8L10.6,10.9z"
                  />
                </g>
              </svg>
            </a>
            <a
              href="https://apps.apple.com/app/id1552606148"
              target="_blank"
              rel="noopener noreferrer">
              <svg
                x="0px"
                y="0px"
                viewBox="0 0 17 20"
                style={{ enableBackground: 'new 0 0 17 20' }}>
                <g>
                  <path
                    className="store-icon"
                    d="M12.1,0c-1,0.1-2.3,0.7-3,1.6c-0.7,0.8-1.2,2-1,3.1c1.1,0,2.3-0.7,3-1.5C11.8,2.4,12.3,1.2,12.1,0z"
                  />
                  <path
                    className="store-icon"
                    d="M16.3,6.6c-1-1.3-2.4-2-3.8-2c-1.8,0-2.5,0.8-3.7,0.8C7.5,5.5,6.5,4.6,5,4.6c-1.5,0-3.1,0.9-4.1,2.5
		C-0.5,9.3-0.3,13.5,2,17c0.8,1.3,1.9,2.7,3.4,2.7c1.3,0,1.6-0.8,3.4-0.8c1.7,0,2.1,0.8,3.4,0.8c1.4,0,2.6-1.6,3.4-2.9
		c0.6-0.9,0.8-1.4,1.3-2.4C13.5,13.2,13,8.4,16.3,6.6z"
                  />
                </g>
              </svg>
            </a>
          </p>
        </div>
        <div className="hero-images">
          <div className="center-hero-image">
            <StaticImage
              placeholder="blurred"
              src="../images/hero-man.png"
              alt="Hero Image"
            />

            <div className="floating-box">
              <Parallax y={[-80, 80]} tagOuter="div" className="hero-parallax">
                <Slider {...settingsHeroCards}>
                  <img src="/img/card-1.svg" alt="hero card" />
                  <img src="/img/card-3.png" alt="hero card" />
                  <img src="/img/card-5.png" alt="hero card" />
                </Slider>
              </Parallax>
              <Parallax y={[30, -30]} tagOuter="div" className="hero-parallax">
                <Slider {...settingsHeroCards}>
                  <img src="/img/card-2.svg" alt="hero card" />
                  <img src="/img/card-4.png" alt="hero card" />
                  <img src="/img/card-6.png" alt="hero card" />
                </Slider>
              </Parallax>
            </div>
          </div>
          <Parallax className="hero-line-img" y={[-20, 20]} tagOuter="div">
            <StaticImage
              placeholder="blurred"
              src="../images/hero-1.png"
              alt="Hero Image"
            />
          </Parallax>
        </div>
      </div>
    </section>
  );
};

const Index = (props) => {
  const [featureIndex, setFeatureIndex] = useState(0);

  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  const [currentRoleAccordionIndex] = useState(0);

  const setCurrentRole = (index) => () => setCurrentRoleIndex(index);

  const features = useMemo(
    () => [
      {
        id: 0,
        title: 'Mobile First',
        image: '/img/mobile-first-icon.svg',
        cover: '/img/mobile-first.svg',
        content: (
          <p>
            On-site teams are equipped with an easy to use mobile app for
            reporting, communication, and real-time collaborations.
          </p>
        ),
      },
      {
        id: 1,
        title: 'Real-time Collaboration',
        image: '/img/realtime-collaboration.svg',
        cover: '/img/real-time-collaboration.svg',
        content: (
          <p>
            Get immediate photo updates, assign tasks, and delegate work to
            ensure smooth workflow throughout the project.
          </p>
        ),
      },
      {
        id: 2,
        title: 'Document storage',
        image: '/img/documentation.svg',
        cover: '/img/documentation-storage.svg',
        content: (
          <p>
            Store useful documents like photos, plans, reports, and other
            information to keep everyone on the same page.
          </p>
        ),
      },
    ],
    [],
  );

  const userRoles = useMemo(
    () => [
      {
        id: 0,
        title: 'For CXOs/Owners/MDs',
        tabName: 'For CXOs/Owners/MDs',
        accordion: (
          <Accordion
            className="accordion"
            selectedIndex={currentRoleAccordionIndex}>
            <div
              data-img="/img/check.svg"
              data-header="Inspect site progress remotely:"
              className="accordion-item">
              <p>
                Get and share real-time updates about overall progress with the
                wider team
              </p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Solve problems quickly and efficiently:"
              className="accordion-item">
              <p>
                Track and resolve on-site and in-office bottlenecks &amp; issues
              </p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Maintain estimated timelines &amp; budget:"
              className="accordion-item">
              <p>
                Review site images and reports for clear progress, material, and
                labour updates
              </p>
            </div>
          </Accordion>
        ),
        image: (
          <>
            <img src="/img/managing-director.png" alt="managing-director" />
            <p>CXOs/Owners/MDs</p>
          </>
        ),
      },
      {
        id: 1,
        title: 'Project Managers',
        tabName: 'Project Managers',
        accordion: (
          <Accordion
            className="accordion"
            selectedIndex={currentRoleAccordionIndex}>
            <div
              data-img="/img/check.svg"
              data-header="Get a bird’s eye view:"
              className="accordion-item">
              <p>
                Supervise and regulate the tasks assigned to ensure steady
                progress
              </p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Keep the workflow going:"
              className="accordion-item">
              <p>
                Track, resolve, and update owners about on-site or in-office
                bottlenecks
              </p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Inspect daily progress:"
              className="accordion-item">
              <p>
                Review on-site progress reports sent by the site supervisors
              </p>
            </div>
          </Accordion>
        ),
        image: (
          <>
            <img src="/img/office-teams.png" alt="office-teams" />
            <p>Project Managers</p>
          </>
        ),
      },
      {
        id: 2,
        title: 'Site Supervisors/Engineers',
        tabName: 'Site Supervisors/Engineers',
        accordion: (
          <Accordion
            className="accordion"
            selectedIndex={currentRoleAccordionIndex}>
            <div
              data-img="/img/check.svg"
              data-header="Manage and store documents:"
              className="accordion-item">
              <p>
                Store site work, material, labour, or invoices as documents and
                pictures
              </p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Keep owners in the loop:"
              className="accordion-item">
              <p>Send owners &amp; managers real-time progress reports</p>
            </div>
            <div
              data-img="/img/check.svg"
              data-header="Supervise manpower &amp; inventory:"
              className="accordion-item">
              <p>
                Share updates about deployed skilled &amp; unskilled labour and
                material inventory
              </p>
            </div>
          </Accordion>
        ),
        image: (
          <>
            <img src="/img/site-teams.png" alt="site-teams" />
            <p>Site Supervisors</p>
          </>
        ),
      },
    ],
    [],
  );

  const featureAutoPlay = useCallback(() => {
    setFeatureIndex((state) => (state === 2 ? 0 : state + 1));
    setCurrentRoleIndex((state) => (state === 2 ? 0 : state + 1));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      featureAutoPlay();
    }, 6000);

    return () => {
      clearInterval(interval);
    };
  }, [featureAutoPlay]);

  return (
    <Layout {...props}>
      <SEO
        title="Making communication easier for construction industry | Powerplay"
        description="Powerplay is India's 1st collaboration app built for construction companies that helps teams work together on infrastructure projects easily."
      />
      <HeroSection />
      <section className="clients-home">
        <div className="container">
          <h2>Trusted by 100+ leading companies</h2>
          <Slider {...clientSlider}>
            <div>
              <div className="client-list">
                <img
                  src="/img/cushman-and-wakefield.png"
                  alt="cushman-and-wakefield.png"
                />

                <img src="/img/epic.png" alt="epic" />

                <img src="/img/tata-power.png" alt="tata-power" />

                <img src="/img/tornado.png" alt="tornado" />
              </div>
            </div>
            <div>
              <div className="client-list">
                <img src="/img/flipspaces.png" alt="flipspaces" />

                <img src="/img/m-solar.png" alt="m-solar" />
                <img src="/img/hasan-solar.png" alt="hasan-solar" />
                <img src="/img/dnova.png" alt="dnova" />
              </div>
            </div>
          </Slider>
        </div>
      </section>
      <section
        className="pad100"
        style={{ backgroundColor: '#F8F9FB', paddingBottom: '0' }}>
        <div className="container">
          <h2 className="heading text-center">How does Powerplay help?</h2>
          <div className="help-sec-main">
            <div className="flex-row">
              <div className="col-4 offset-1 col">
                <div className="help-home-accordions">
                  <Accordion
                    className="accordion"
                    selectedIndex={featureIndex}
                    onChange={setFeatureIndex}>
                    {features.map(({ id, image, title, content }) => (
                      <div
                        key={id}
                        data-img={image}
                        data-header={title}
                        className="accordion-item">
                        {content}
                      </div>
                    ))}
                  </Accordion>
                </div>
                <Link to="/product/" className="link-unstyled">
                  Explore Powerplay
                  <svg
                    x="0px"
                    y="0px"
                    viewBox="0 0 12 11"
                    style={{ enableBackground: 'new 0 0 12 1' }}>
                    <g>
                      <polyline
                        className="arrow-line"
                        points="1,1 6,5.5 1,10"
                      />
                      <polyline
                        className="arrow-line"
                        points="6,1 11,5.5 6,10"
                      />
                    </g>
                  </svg>
                </Link>
              </div>
              <div className="col-5 offset-1 col">
                <div className="help-img">
                  {features.map(({ id, cover }) => (
                    <img
                      key={id}
                      className={id === featureIndex ? 'show' : 'hide'}
                      src={cover}
                      alt="How does Powerplay help"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="pad100">
        <div className="container">
          <div className="before-after-main">
            <h2 className="heading text-center">
              Fastest, Safest, Simplest &amp; Free
            </h2>
            <p className="text-center">
              Powerplay helps you manage your construction projects by offering
              you and your team complete clarity through on-site updates. Easy
              collaboration keeps everyone in the loop, preventing rework and
              miscommunication.
            </p>
            <div className="flex-row">
              <div className="col col-5 offset-1">
                <div className="single-before-after">
                  <h3>
                    Without Powerplay
                    <br />
                    <img src="/img/sad.svg" alt="" />
                  </h3>
                  <img
                    src="/img/before-powerplay.svg"
                    alt="without powerplay"
                  />
                </div>
              </div>
              <div className="col col-5">
                <div className="single-before-after">
                  <h3>
                    With Powerplay
                    <br />
                    <img src="/img/happy.svg" alt="" />
                  </h3>

                  <img src="/img/after-powerplay.svg" alt="with powerplay" />
                </div>
              </div>
            </div>
            <div className="text-center" style={{ marginTop: '55px' }}>
              <Link className="button" to="/about/">
                Read about us
              </Link>
            </div>
          </div>
        </div>
      </section>
      <section className="powerplay-projects-main bg-lightblue">
        <div className="container">
          <h2 className="heading text-center">Projects done on Powerplay</h2>
          <div className="work-slider">
            <Slider {...settingsWork}>
              <div>
                <div className="single-project">
                  <img src="/img/commercial.png" alt="commerical" />
                  <h3>Commercial</h3>
                  <p>Google Govo, Phoenix Aquila, NIIT</p>
                </div>
              </div>
              <div>
                <div className="single-project">
                  <img src="/img/residential.png" alt="residential" />
                  <h3>Residential</h3>
                  <p>Bhownani Residence, Garima Mitesh Residence</p>
                </div>
              </div>
              <div>
                <div className="single-project">
                  <img src="/img/interior.png" alt="interior" />
                  <h3>Interior</h3>
                  <p>Comptel, Dover, Oneplus</p>
                </div>
              </div>
            </Slider>
          </div>
        </div>
      </section>
      <section className="features-main">
        <div className="container">
          <h2 className="heading white-heading text-center">
            Powerplay helps you manage your construction projects, all from a
            single app.
          </h2>
          <div className={`feature-block active-${currentRoleIndex + 1}`}>
            <div className="flex-row">
              <div className="col-5 offset-1 col">
                <div className="visible-mobile">
                  <div className="feature-accordion-tabs">
                    {userRoles.map(({ id, tabName }) => (
                      <button
                        key={id}
                        type="button"
                        className={currentRoleIndex === id ? 'active' : ''}
                        onClick={setCurrentRole(id)}>
                        {tabName}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="feature-accordion-main">
                  {userRoles.map(({ id, title, accordion }) => (
                    <div key={id} className="feature-accordion-block">
                      <h4>{title}</h4>
                      {accordion}
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="col-5 offset-1 col hidden-mobile"
                style={{ position: 'relative' }}>
                <p className="click-to-view">
                  Click to view <img src="/img/curved-arrow.svg" alt="" />
                </p>
                <div className="feature-img-block">
                  {userRoles.map(({ id, image }) => (
                    <button
                      key={id}
                      type="button"
                      className={`single-feature-img ${
                        currentRoleIndex === id ? 'active' : ''
                      }`}
                      onClick={setCurrentRole(id)}>
                      {image}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="container">
          <div className="secure-block">
            <div className="container">
              <h2
                className="heading-big"
                style={{ maxWidth: '844px', margin: '0 auto 40px' }}>
                Safe and Secured to digitise the Indian construction industry
                <img
                  src="/img/indian-flag.png"
                  style={{
                    width: '46px',
                    display: 'inline-block',
                    verticalAlign: 'baseline',
                    marginLeft: '10px',
                  }}
                  alt="India"
                />
              </h2>
              <div className="flex-row">
                <div className="col col-4 offset-2">
                  <div className="single-secure-block">
                    <img src="/img/safe-and-secure.png" alt="safe-and-secure" />
                    <h3>Safe, secure, and free</h3>
                    <p>
                      Over 150,000 users trust Powerplay&apos;s free app for
                      more than 20,000 projects. Your data is encrypted and 100%
                      backup ready.
                    </p>
                  </div>
                </div>
                <div className="col col-4">
                  <div className="single-secure-block">
                    <img src="/img/india-map.jpg" alt="india-map" />
                    <h3>Made in India, For India</h3>
                    <p>
                      Built by the experts who understand the nuances of
                      construction projects in India. Powerplay is easy to use,
                      even for those who aren’t experienced with mobile apps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Testimonial />
      <CareerBlock />
      <section className="stats-home">
        <div className="container">
          <div className="flex-row">
            <div className="col-4 col">
              <div className="single-stat">
                <h4>
                  <Counter start={0} end={100} />
                  K+
                </h4>
                <p>App Downloads</p>
              </div>
            </div>
            <div className="col-4 col">
              <div className="single-stat">
                <h4>
                  <Counter start={0} end={500} duration={0.1} />+
                </h4>
                <p>Companies Helped</p>
              </div>
            </div>
            <div className="col-4 col">
              <div className="single-stat">
                <h4>
                  <Counter
                    start={0}
                    end={4.2}
                    step={0.1}
                    duration={0.25}
                    decimals={1}
                  />
                  /5.0
                </h4>
                <p>App Rating on Playstore</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
